import React, { useState, useEffect } from 'react';
import Star from '../../components/star';
import supabase from '../../supabase';
import FullPageLoader from '../../components/FullPageLoader';
import Sidebanner from './sidebanner';
import Papa from 'papaparse';
import { validateAndSuppress } from '../../utils/zeroBounce'; 
import { useAuth } from '../../context/AuthContext';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import emailsvg from '../../assets/invitesvg.svg'

const BURNER_DOMAINS = new Set(['10minutemail.com', 'tempmail.com', 'throwawaymail.com', 'guerrillamail.com', 'mailinator.com', 'yopmail.com', 'dispostable.com', 'getnada.com', 'maildrop.cc', 'fakeinbox.com', 'trashmail.com', 'mintemail.com', 'mytemp.email', 'spambox.us', 'mailcatch.com', 'easytrashmail.com', 'anonymbox.com', 'spamgourmet.com', 'mailnesia.com', 'trashmail.net', 'temp-mail.org', '0wnd.net', 'mail-temporaire.fr', 'tempinbox.com', 'emailondeck.com', 'moakt.com', 'spambox.info', 'jetable.org', 'tempemail.co', 'getairmail.com', 'burnermail.io', 'sharklasers.com', 'spam4.me', 'mailnull.com', 'fake-mail.net', 'temp-mail.io', 'disposablemail.com', 'mail-temporaire.com', 'trashmail.de', 'tempail.com', 'mailtothis.com', 'spambog.com', 'mytrashmail.com', 'throwawaymail.net', 'tempemail.net', 'spamdecoy.net', 'mailinater.com', 'temp-mail.cf', 'mail-temporaire.net', 'gettempmail.com', 'temp-mail.pro', 'disposableemailaddresses.com', 'spamfree24.org', 'mail-temporaire.org', 'tempomail.fr', 'trashmail.ws', 'tempmailo.com', 'mail4trash.com', 'spambox.xyz', 'tempemailaddress.com', '10minutemail.net', 'mail-temporaire.co', 'temp-mail.ru', 'disposableaddress.com', 'spamavert.com', 'mail-temporaire.info', 'tempemail.biz', 'trashmail.io', 'tempmailaddress.com', 'mail-temporaire.biz', 'tempinbox.xyz', 'spambox.us.com', 'mail-temporaire.co.uk', 'temp-mail.us', 'disposableinbox.com', 'spamfighter.com', 'mail-temporaire.us', 'tempemail.org', 'trashmail.org', 'tempmail.net', 'mail-temporaire.eu', 'temp-mail.eu', 'disposableemails.com', 'spamherelots.com', 'mail-temporaire.eu.com', 'tempemail.info', 'trashmail.co', 'mail-temporaire.info.com', 'temp-mail.info', 'disposableemail.net', 'spamgourmet.de']);
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Homepage() {
  const navigate = useNavigate();
  const { user, business, loading: authLoading, subscription, hasActiveAccess } = useAuth();
  const [loading, setLoading] = useState(true);
  const [invites, setInvites] = useState([]);
  const [delayDays, setDelayDays] = useState(7); 
  
  // Upload & Audit States
  const [isUploading, setIsUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [processedInvites, setProcessedInvites] = useState([]); 
  const [statusMsg, setStatusMsg] = useState({ type: '', text: '' });
  
  // Quota States
  const [monthlyInviteCount, setMonthlyInviteCount] = useState(0);

  const isTrial = subscription?.status === 'trialing';
  const plan = subscription?.plan_type; // 'trial', 'standard', 'premium'
  const status = subscription?.plan_type; // 'trial', 'standard', 'premium'
const isCanceled = subscription?.status === 'canceled';

const INVITE_LIMIT = isTrial 
  ? 100 
  : isCanceled 
    ? 0 
    : (plan === 'premium') 
      ? 1500 
      : (plan === 'standard') 
        ? 500 
        : 0;
        
const location = useLocation();


useEffect(() => {
    const params = new URLSearchParams(location.search);
    
    if (params.get('payment') === 'success') {
      // 1. Show the success message
      alert("Payment Successful! Welcome to your new plan.");

      // 2. Clean up the URL (Removes the ?payment=success so it doesn't alert again on refresh)
      navigate('/business/home', { replace: true });
      
      // 3. Optional: Refresh your Auth context data here if needed
    }
  }, [location, navigate]);

  // Trial Logic
  const trialEndDate = subscription?.next_bill_date ? new Date(subscription.next_bill_date) : null;
  const daysRemaining = trialEndDate ? Math.ceil((trialEndDate - new Date()) / (1000 * 60 * 60 * 24)) : 0;

  useEffect(() => {
    if (authLoading || !user) return;

    async function fetchPageData() {
      setLoading(true);

      // 1. Calculate the start of the current month (The "Automatic Reset")
 const now = new Date();
const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

      // 2. Fetch the 10 most recent invites for the table
      const { data: recentInvites } = await supabase
        .from('review_invites')
        .select('*')
        .eq('business_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);
      
      setInvites(recentInvites || []);

      // 3. Fetch the TOTAL count for THIS month (The Quota Counter)
      const { count: usage } = await supabase
        .from('review_invites')
        .select('*', { count: 'exact', head: true })
        .eq('business_id', user.id)
        .gte('created_at', firstOfMonth); 

      setMonthlyInviteCount(usage || 0);
      setLoading(false);
    }

    fetchPageData();
  }, [user, authLoading]);

  const customRound = (rating) => {
    const rounded = parseFloat(rating);
    if (isNaN(rounded)) return 0;
    const decimal = rounded % 1;
    if (decimal < 0.25) return Math.floor(rounded);
    if (decimal < 0.75) return Math.floor(rounded) + 0.5;
    return Math.ceil(rounded);
  };

  const getStarColor = (rounded) => {
    if (rounded >= 4.8) return 'bg-five'; 
    if (rounded >= 4.3) return 'bg-fourhalf'; 
    if (rounded >= 3.8) return 'bg-four'; 
    if (rounded >= 3.3) return 'bg-threehalf';   
    if (rounded >= 2.8) return 'bg-three';   
    if (rounded >= 2.3) return 'bg-twohalf';   
    if (rounded >= 1.8) return 'bg-two';   
    if (rounded >= 1.3) return 'bg-onehalf';   
    if (rounded >= 0.1) return 'bg-one';
    else return 'bg-gray-300';   
  };

  const changeDelay = (days) => {
    setDelayDays(days);
    setStatusMsg({ type: 'success', text: `Mode: ${days}-day delay selected.` });
    setTimeout(() => setStatusMsg({ type: '', text: '' }), 2000);
  };

  const handleFileSelection = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        let validRows = [];
        results.data.forEach(row => {
          const name = (row.customer_name || row.Name || row.name || '').trim();
          const email = (row.customer_email || row.Email || row.email || '').trim().toLowerCase();
          if (!name || !email) return;

          if (EMAIL_REGEX.test(email) && !BURNER_DOMAINS.has(email.split('@')[1])) {
            validRows.push({
              customer_name: name,
              customer_email: email,
              business_id: user.id,
              status: 'pending'
            });
          }
        });
        
        const uniqueInCsv = Array.from(new Map(validRows.map(item => [item.customer_email, item])).values());
        setProcessedInvites(uniqueInCsv);
        setPendingFile(file);
      }
    });
  };

  const confirmUpload = async () => {
    if (!processedInvites.length || !user) return;
    setIsUploading(true);
    let ultraCleanInvites = [];

    try {
      // 1. Audit Emails via ZeroBounce
      for (let i = 0; i < processedInvites.length; i++) {
        const audit = await validateAndSuppress(processedInvites[i].customer_email);
        if (audit.isValid) ultraCleanInvites.push(processedInvites[i]);
      }

      // 2. Prepare Scheduling Logic
      let nextAvailableSlot = new Date();
      nextAvailableSlot.setDate(nextAvailableSlot.getDate() + delayDays);

      const finalInvites = ultraCleanInvites.map((invite, index) => ({
        ...invite,
        scheduled_for: new Date(nextAvailableSlot.getTime() + (index * 15 * 60000)).toISOString()
      }));

      // 3. Database Insert (The DB Trigger will verify the quota again)
      const { error } = await supabase.from('review_invites').insert(finalInvites);
      if (error) throw error;

      // 4. Update UI State Immediately
      setMonthlyInviteCount(prev => prev + finalInvites.length); // THIS UPDATES THE QUOTA BAR
      setInvites(prev => [...finalInvites.slice(0, 10), ...prev].slice(0, 10));
      setStatusMsg({ type: 'success', text: `Verified & Queued ${finalInvites.length} customers.` });
      setPendingFile(null);
    } catch (err) {
      console.error(err);
      setStatusMsg({ type: 'error', text: err.message || 'Process failed.' });
    } finally {
      setIsUploading(false);
    }
  };

  const score = business?.trustscore || 0;
  const displayScore = Number(score).toFixed(1);
  const starValue = customRound(score);
  const starColor = getStarColor(starValue);
  const remainingInvites = Math.max(0, INVITE_LIMIT - monthlyInviteCount);

  if (authLoading) return <FullPageLoader />;

  return (
    <div className='min-h-screen bg-[#f9fafb] pt-[90px] lg:pt-[100px] xl:pt-[120px] w-full flex flex-col px-[22px] lg:pr-[250px] xl:pr-[350px] font-poppins pb-20'>
      <Helmet>
        <title>Dashboard | Sitemanifest</title> 
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* --- TRIAL STATUS BANNER --- */}
      {isTrial && hasActiveAccess && (
        <div className='max-w-[750px] mx-auto w-full mb-6'>
          <div className='bg-gradient-to-r from-blue-600 to-indigo-700 rounded-3xl p-1 shadow-lg shadow-blue-100'>
            <div className='bg-white/95 rounded-[22px] px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4'>
              <div className='flex items-center gap-4'>
                <div className='w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600'>
                   <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </div>
                <div>
                  <h4 className='text-xs font-black text-gray-900 uppercase tracking-tight'>Free Trial Active</h4>
                  <p className='text-[11px] text-gray-500 font-medium'>
                    Trial features expire in <span className='text-blue-600 font-bold'>{daysRemaining} days</span> ({trialEndDate.toLocaleDateString()})
                  </p>
                </div>
              </div>
              <button 
                onClick={() => navigate('/business/pricing')}
                className='bg-blue-600 text-white px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-700 transition-all shadow-md active:scale-95'
              >
                Upgrade Now
              </button>
            </div>
          </div>
        </div>
      )}

<div className='flex flex-col md:flex-row items-center w-full max-w-[750px] mx-auto justify-between gap-6 mb-10'>
  
  {/* Left Side: Context & Title */}
  <div className='flex flex-col text-center md:text-left'>
    <h1 className='text-3xl font-black text-[#1a1a1a] tracking-tight'>
      Dashboard
    </h1>
    <div className='flex items-center justify-center md:justify-start gap-2 mt-1'>
      <p className='text-slate-500 text-sm font-medium'>
        Managing reputation for 
      </p>
      <span className='px-2 py-0.5 bg-blue-50 text-blue-600 text-xs font-bold rounded-md border border-blue-100 shadow-sm'>
        {business?.title}
      </span>
    </div>
  </div>

  {/* Right Side: Action Button */}
  <button 
    onClick={() => window.open(`/company/${business?.www}`, '_blank', 'noopener,noreferrer')}
    className="group relative flex items-center justify-center gap-3 px-6 py-3 bg-[#32596E] hover:bg-[#2a4a5c] w-full md:w-auto min-w-[200px] text-white rounded-xl font-bold text-sm transition-all duration-200 shadow-[0_10px_20px_-5px_rgba(50,89,110,0.3)] hover:shadow-[0_15px_25px_-5px_rgba(50,89,110,0.4)] active:scale-[0.98]"
  >
    <span>Preview Public Page</span>
    {/* Minimalist External Link Icon */}
    <svg 
      className="w-4 h-4 opacity-70 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" 
      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"
    >
      <path d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
    </svg>
  </button>
</div>

      <div className='relative max-w-[750px] mx-auto w-full mb-8'>
        <div className='bg-white border border-gray-100 rounded-[32px] p-8 lg:p-10 shadow-sm'>
          
          <div className='flex flex-col md:flex-row items-center justify-between gap-8 pb-8 border-b border-gray-100'>
            <div className='flex flex-col items-center md:items-start'>
              <div className='flex items-baseline gap-2'>
                <span className='text-7xl font-black text-[#1a1a1a]'>{displayScore}</span>
                <span className='text-gray-400 font-bold'>/ 5</span>
              </div>
              <div className='flex flex-row gap-1.5 mt-4'>
                {Array(5).fill().map((_, i) => (
                  <Star
                    key={i}
                    boxwidth='w-7 lg:w-9'
                    boxheight='h-7 lg:h-9'
                    width='w-5 lg:w-6'
                    bgColor={i < Math.floor(starValue) ? starColor : 'bg-gray-200'}
                    half={i === Math.floor(starValue) && starValue % 1 >= 0.5}
                    halfStar={starColor}
                  />
                ))}
              </div>
            </div>
            <div className='text-center md:text-right'>
              <p className='text-gray-400 text-[10px] font-bold uppercase tracking-widest'>Total Reviews</p>
              <p className='text-3xl font-black text-[#1a1a1a]'>{Number(business?.reviews || 0).toLocaleString()}</p>
            </div>
          </div>

          <div className='mt-8 flex flex-col gap-6'>

            <div className='flex flex-row items-center gap-[12px]'>
              <img className='w-[24px]' src={emailsvg} alt="Email Icon" />
              <span className='font-staat text-[18px] text-[#323232]'>EMAIL INVITATION</span>
            </div>
      
            {/* Delay & Quota Section */}
            <div className='bg-gray-50/50 p-6 rounded-2xl border border-gray-100'>
              <div className='flex items-center justify-between mb-4'>
                <h4 className='text-[11px] font-black text-gray-400 uppercase tracking-widest'>Invitation Delay</h4>
              </div>
              <div className='grid grid-cols-4 gap-2'>
                {[1, 3, 7, 14].map((d) => (
                  <button key={d} onClick={() => changeDelay(d)} className={`py-3 text-[11px] font-black rounded-xl transition-all border ${delayDays === d ? 'bg-white text-blue-600 shadow-sm border-blue-100' : 'bg-transparent text-gray-400 border-transparent hover:bg-gray-100'}`}>
                    {d} {d === 1 ? 'Day' : 'Days'}
                  </button>
                ))}
              </div>
            </div>

            <div className='flex flex-col md:flex-row items-center justify-between gap-6 pt-4'>
              <div className='flex-1 text-center md:text-left'>
                <h4 className='text-sm font-bold text-gray-800 uppercase tracking-tight'>Quota Usage</h4>
                <p className={`text-2xl font-black mt-1 ${remainingInvites <= 0 ? 'text-red-500' : 'text-blue-600'}`}>
                   {remainingInvites.toLocaleString()} <span className='text-[10px] uppercase text-gray-400 font-bold'>Remaining</span>
                </p>
              </div>

<div className='flex flex-col items-center md:items-end gap-3 w-full md:w-auto'>
  {/* CSV HEADER INSTRUCTIONS */}
  {!pendingFile && (
    <div className="mb-2 text-right">
      <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest flex items-center justify-center md:justify-end gap-1.5">
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
        Required Headers: <span className="text-blue-600">"customer_name","customer_email"</span>
      </p>
    </div>
  )}

  {!pendingFile ? (
    <label className={`w-full md:w-auto px-10 py-4 rounded-2xl font-black text-xs text-center transition-all cursor-pointer shadow-lg active:scale-95 ${remainingInvites <= 0 ? 'bg-gray-100 text-gray-300' : 'bg-[#1a1a1a] text-white hover:bg-blue-600 shadow-blue-100'}`}>
      UPLOAD CSV
      <input type="file" accept=".csv" className="hidden" onChange={handleFileSelection} disabled={remainingInvites <= 0} />
    </label>
  ) : (
    <div className='flex flex-col items-center md:items-end gap-2 w-full'>
      <div className='flex gap-2 w-full'>
          <button onClick={() => setPendingFile(null)} className='px-6 py-4 rounded-2xl font-black text-[10px] bg-gray-100 text-gray-500 uppercase transition-all hover:bg-gray-200'>Cancel</button>
          <button onClick={confirmUpload} disabled={isUploading} className='px-10 py-4 rounded-2xl font-black text-xs bg-blue-600 text-white shadow-lg transition-all hover:bg-blue-700 active:scale-95 disabled:opacity-50'>
           {isUploading ? 'AUDITING...' : `START AUDIT & QUEUE`}
          </button>
      </div>
    </div>
  )}
</div>
            </div>
          </div>
        </div>
      </div>

      <div className='max-w-[750px] mx-auto w-full'>
        <div className='flex items-center justify-between mb-4 px-2'>
          <h3 className='text-sm font-black text-gray-800 uppercase tracking-widest'>Queue Monitor</h3>
        </div>
        <div className='bg-white border border-gray-100 rounded-[32px] overflow-hidden shadow-sm'>
          <table className='w-full text-left border-collapse'>
            <thead>
              <tr className='bg-gray-50/50 border-b border-gray-100'>
                <th className='px-6 py-4 text-[10px] font-bold text-gray-400 uppercase'>Customer</th>
                <th className='px-6 py-4 text-[10px] font-bold text-gray-400 uppercase'>Status</th>
              </tr>
            </thead>
            <tbody>
              {invites.length > 0 ? invites.map((invite, idx) => (
                <tr key={idx} className='border-b border-gray-50 last:border-0 hover:bg-gray-50/30 transition-colors'>
                  <td className='px-6 py-4'>
                    <p className='text-xs font-bold text-gray-800'>{invite.customer_name}</p>
                    <p className='text-[10px] text-gray-400'>{invite.customer_email}</p>
                  </td>
                  <td className='px-6 py-4'>
                    <span className={`text-[9px] font-bold px-2 py-1 rounded-full uppercase ${invite.status === 'sent' ? 'bg-blue-100 text-blue-600' : 'bg-orange-100 text-orange-600'}`}>
                      {invite.status}
                    </span>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="2" className='px-6 py-12 text-center text-gray-400 text-xs italic font-medium'>No invitations found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Sidebanner />
    </div>
  );
}